
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { LoadingPlanListTableRowData } from "@/views/components/DeclarationDocumentTable.vue";
import {
  createLoadingPlanList,
  getLoadingPlanLists,
  getLoadingPlanProduct,
  previewCustomsDeclaration,
  updateLoadingPlanList,
} from "@/api/booking-order";
import { LoadingPlanType } from "@/views/sales-management/SalesBookingOrderManagement.vue";
import { CustomsPackingModel, ProductInfoModel } from "@/model/HenganLoadingPlanModels";
import { isEmpty, toUpper } from "lodash";
import CustomsDeclarationPreviewDialog, {
  CustomsDeclarationPreviewItem,
} from "./CustomsDeclarationPreviewDialog.vue";

@Component({
  name: "AddDeclarationDocumentDialog",
  components: { CustomsDeclarationPreviewDialog },
})
export default class extends Vue {
  @Prop({ default: false }) private visible: boolean;
  @Prop({ default: "" }) private bookingOrderId: string;
  @Prop({ default: "" }) private bookingOrderCode: string;
  @Prop({ default: "" }) private customerId: string;
  @Prop({ default: "" }) private customerAbbr: string;

  private loading = false;
  private keyword = "";
  private loadingPlanList: LoadingPlanList = new LoadingPlanList();
  private loadingPlans: LoadingPlanListTableRowData[] = [];

  private detailDrawerVisible = false;
  private detailLoading = false;
  private activeDetailPlan: LoadingPlanListTableRowData | null = null;
  private detailProducts: ProductInfoModel[] = [];

  private previewVisible = false;
  private previewLoading = false;
  private previewItems: CustomsDeclarationPreviewItem[] = [];

  get selectedPlans(): LoadingPlanListTableRowData[] {
    return this.loadingPlans.filter((value) => value.select);
  }

  get selectedTotalVolume(): string {
    const total = this.selectedPlans.reduce((sum, item) => {
      const raw = String(item.totalVolume || "").replace(/[^\d.-]/g, "");
      const value = Number(raw);
      return sum + (Number.isFinite(value) ? value : 0);
    }, 0);
    return total.toFixed(4);
  }

  get availablePlans(): LoadingPlanListTableRowData[] {
    return this.loadingPlans
      .filter((value) => !value.select)
      .filter((value) => this.matchKeyword(value));
  }

  get emptyCandidateText(): string {
    const hasUnselected = this.loadingPlans.some((value) => !value.select);
    if (!hasUnselected) {
      return "暂无可选报关单据";
    }
    return "没有匹配的报关单据";
  }

  get detailDrawerTitle(): string {
    if (!this.activeDetailPlan) {
      return "查看详情";
    }
    return `查看详情 · ${this.activeDetailPlan.code}`;
  }

  @Watch("visible", { immediate: true })
  private async onVisibleChange(val: boolean) {
    if (val && this.bookingOrderId) {
      await this.loadData();
    }
  }

  private typeDesc(item: LoadingPlanListTableRowData): string {
    return item.type && item.type.desc ? item.type.desc : "-";
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  private highlightMatch(text: string): string {
    const source = text || "";
    const keyword = this.keyword.trim();
    if (!keyword) {
      return this.escapeHtml(source);
    }
    const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const reg = new RegExp(escapedKeyword, "gi");
    let result = "";
    let lastIndex = 0;
    let match: RegExpExecArray | null = reg.exec(source);
    while (match) {
      result += this.escapeHtml(source.slice(lastIndex, match.index));
      result += `<em class="search-hit">${this.escapeHtml(match[0])}</em>`;
      lastIndex = match.index + match[0].length;
      if (match[0].length === 0) {
        reg.lastIndex += 1;
      }
      match = reg.exec(source);
    }
    result += this.escapeHtml(source.slice(lastIndex));
    return result;
  }

  private matchKeyword(row: LoadingPlanListTableRowData): boolean {
    if (isEmpty(this.keyword)) {
      return true;
    }
    const keyword = toUpper(this.keyword.trim());
    const code = toUpper(row.code || "");
    const factory = toUpper(row.loadingPlace || "");
    return code.includes(keyword) || factory.includes(keyword);
  }

  private async loadData() {
    this.loading = true;
    this.loadingPlans = [];
    this.loadingPlanList = new LoadingPlanList();
    this.keyword = "";
    try {
      const data: any = await getLoadingPlanLists(this.bookingOrderId);
      this.loadingPlanList = data;
      const unusedLoadingPlans = (this.loadingPlanList.unusedLoadingPlans || []).map(
        (value) => this.generateDeclarationDocumentTableRowData(value, false)
      );
      const usedLoadingPlans = (this.loadingPlanList.loadingPlans || []).map((value) =>
        this.generateDeclarationDocumentTableRowData(value, true)
      );
      this.loadingPlans = unusedLoadingPlans.concat(usedLoadingPlans);
    } finally {
      this.loading = false;
    }
  }

  private generateDeclarationDocumentTableRowData(
    loadingPlan: LoadingPlan,
    selected: boolean
  ): LoadingPlanListTableRowData {
    const rowData: LoadingPlanListTableRowData = new LoadingPlanListTableRowData();
    rowData.id = loadingPlan.id;
    rowData.code = loadingPlan.code;
    rowData.createdDate = loadingPlan.createdAt;
    rowData.containerType = loadingPlan.containerTypeInfos.join(",");
    rowData.type = loadingPlan.loadingPlanType;
    rowData.category = loadingPlan.category;
    rowData.loadingPlace = loadingPlan.factoryNames;
    rowData.containerInfo = loadingPlan.containerTypeInfos.join(",");
    rowData.totalProductQuantity = loadingPlan.totalPCS;
    rowData.totalAmount = loadingPlan.totalAmount;
    rowData.totalVolume = loadingPlan.volumes;
    rowData.select = selected;
    return rowData;
  }

  private addToSelected(row: LoadingPlanListTableRowData) {
    row.select = true;
  }

  private removeFromSelected(row: LoadingPlanListTableRowData) {
    row.select = false;
  }

  private async showDetail(row: LoadingPlanListTableRowData) {
    this.activeDetailPlan = row;
    this.detailProducts = [];
    this.detailDrawerVisible = true;
    this.detailLoading = true;
    try {
      const product: any = await getLoadingPlanProduct(row.id);
      this.detailProducts = product || [];
    } finally {
      this.detailLoading = false;
    }
  }

  private async handleAddLoadingPlanList(confirm: boolean) {
    const selectedLoadingPlans = this.loadingPlans
      .filter((value) => value.select)
      .map((value) => {
        return { id: value.id, loadingPlanType: value.type };
      });
    const data: any = {
      bookingOrderId: this.bookingOrderId,
      confirm,
      loadingPlans: selectedLoadingPlans,
    };
    if (this.loadingPlanList.id) {
      await updateLoadingPlanList(this.loadingPlanList.id, data);
    } else {
      await createLoadingPlanList(data);
    }
    this.handleClose();
    this.$emit("success");
  }

  private handleClose() {
    this.$emit("update:visible", false);
  }

  private async declarationPreview() {
    const factoryPlanIds = this.selectedPlans
      .filter((item) => item.type && item.type.name === "FACTORY_LOADING_PLAN")
      .map((item) => item.id);
    const henganPlanIds = this.selectedPlans
      .filter((item) => item.type && item.type.name === "HENGAN_LOADING_PLAN")
      .map((item) => item.id);

    const data = {
      customerId: this.customerId,
      factoryPlanIds: factoryPlanIds,
      henganPlanIds: henganPlanIds,
    }

    this.previewItems = [];
    this.previewVisible = true;
    this.previewLoading = true;
    try {
      this.previewItems = await previewCustomsDeclaration(data) as any;
    } finally {
      this.previewLoading = false;
    }
  }
}

class LoadingPlanList {
  id: string;
  bookingOrderId: string;
  bookingOrderCode: string;
  customerAbbr: string;
  customerCode: string;
  loadingPlans: LoadingPlan[];
  unusedLoadingPlans: LoadingPlan[];
}

class LoadingPlan {
  id: string;
  loadingPlanType: LoadingPlanType;
  code: string;
  createdAt: string;
  category: string;
  factoryNames: string;
  containerTypeInfos: string[];
  totalPCS: number;
  totalAmount: string;
  volumes: string;
  customerId: string;
  customsPackingSet?: CustomsPackingModel[];
}
